import Meatballs from "@/Icons/Meatballs";
import useClickoutside from "@/Utils/useClickoutside";
import { Button } from "@/components/button";
import { useTrans } from "@/translations";

import { useCallback, useMemo, useRef, useState } from "react";

import { twMerge } from "tailwind-merge";

type TableActionsProps = {
  row: any;
  onEdit: (row: any) => void;
  onDelete: (id: string) => void;
  editDisabled?: boolean;
  deleteDisabled?: boolean;
  className?: string;
};

export const TableActions: React.FC<TableActionsProps> = ({
  row,
  onEdit,
  onDelete,
  editDisabled,
  deleteDisabled,
  className,
}) => {
  const { t } = useTrans();
  const [showButtons, setShowButtons] = useState<boolean>(false);
  const containerWrapper = useRef(null);

  useClickoutside(containerWrapper, () => setShowButtons(false));

  const onMenuButtonClick = useCallback(
    (e: any) => {
      e.preventDefault();
      e.stopPropagation();

      setShowButtons(!showButtons);
    },
    [setShowButtons, showButtons],
  );

  const editButtonClick = useCallback(
    (e: any) => {
      e.preventDefault();
      e.stopPropagation();

      onEdit(row);

      setShowButtons(false);
    },
    [onEdit, row, setShowButtons],
  );

  const deleteButtonClick = useCallback(
    (e: any) => {
      e.preventDefault();
      e.stopPropagation();

      onDelete(row.id);
      setShowButtons(false);
    },
    [onDelete, row, setShowButtons],
  );

  const buttons = useMemo(
    () => [
      {
        title: t("actions:edit"),
        onClick: editButtonClick,
        disabled: editDisabled,
      },
      {
        title: t("actions:delete"),
        onClick: deleteButtonClick,
        style: "text-carrot-red",
        disabled: deleteDisabled,
      },
    ],
    [t, editButtonClick, deleteButtonClick, editDisabled, deleteDisabled],
  );

  return (
    <div
      className={twMerge("relative mr-4 h-8 w-8", className)}
      ref={containerWrapper}
    >
      <div
        onClick={onMenuButtonClick}
        className={twMerge(
          "pointer hover:bg-grey2 flex h-8 w-8 rotate-90 cursor-pointer justify-center rounded-md p-1.5",
          showButtons && "bg-grey3",
        )}
      >
        <Meatballs />
      </div>
      {showButtons && (
        <div className="border-grey2 absolute right-0 z-20 mt-1 border shadow-sm">
          {buttons.map(({ onClick, title, style, disabled }, index) => (
            <Button
              label={title}
              key={title}
              className={twMerge(
                "hover:bg-grey1 flex h-12 w-40 items-center border bg-white pl-4 text-sm",
                index < buttons.length - 1 && "border-b-0",
                style,
              )}
              onClick={onClick}
              disabled={disabled}
              clipped={false}
            />
          ))}
        </div>
      )}
    </div>
  );
};
